/**
 * iter212c — Universal Garage Hive search modal.
 *
 * ONE search box. Type anything you know — jobsheet number, reg,
 * customer, make, model, or any reasonable combo — and pick a
 * jobsheet. The existing exact-import orchestrator runs against the
 * chosen jobsheet number.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Search, Loader2, Cable, X, AlertCircle, CheckCircle2, ArrowRight,
  Car, User, Calendar, ExternalLink, History,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { VehicleHistoryPanel } from "./VehicleHistoryPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 260;
const MIN_CHARS   = 2;

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function StatusPill({ status, isOpen }) {
  if (!status) return null;
  const positive = isOpen || /open|progress|booked/i.test(status);
  const done     = /complet|finish|closed/i.test(status);
  const cls = positive
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : done
      ? "bg-slate-100 text-slate-500 border-slate-200"
      : "bg-amber-100 text-amber-800 border-amber-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${cls}`}>
      {positive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
      {status}
    </span>
  );
}

export function GHSearchModal({ isOpen, onClose, onImported, initialQuery = "" }) {
  const [query, setQuery]     = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [importing, setImporting] = useState(null); // jobsheet_no being imported
  const [diagnostics, setDiag]    = useState(null);
  const [historyReg, setHistoryReg] = useState(null); // reg for open history panel
  const debouncedQ = useDebouncedValue(query, DEBOUNCE_MS);
  const inputRef   = useRef(null);
  const reqIdRef   = useRef(0);     // stale-response guard

  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery || ""); setResults([]); setError(null); setDiag(null);
      setImporting(null);
      // Focus on next tick so the transition can complete.
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [isOpen, initialQuery]);

  useEffect(() => {
    if (!isOpen) return;
    const q = (debouncedQ || "").trim();
    if (q.length < MIN_CHARS) {
      setResults([]); setError(null); setLoading(false); setDiag(null);
      return;
    }
    const myId = ++reqIdRef.current;
    setLoading(true);
    axios
      .get(`${API}/foh/integrations/garage-hive/jobsheets/search`, {
        params: { q, limit: 20 },
      })
      .then((r) => {
        // Stale response? ignore.
        if (myId !== reqIdRef.current) return;
        setResults(r.data.results || []);
        setDiag(r.data.diagnostics || null);
        setError(null);
      })
      .catch((e) => {
        if (myId !== reqIdRef.current) return;
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Garage Hive search failed.");
        setResults([]);
      })
      .finally(() => {
        if (myId !== reqIdRef.current) return;
        setLoading(false);
      });
  }, [debouncedQ, isOpen]);

  const handleSelect = async (row) => {
    if (importing) return;
    setImporting(row.jobsheet_no);
    try {
      // If a Yestimate intake already exists, navigate straight to it.
      if (row.existing_intake_session_id) {
        toast.success(`Opening existing Yestimate intake for ${row.jobsheet_no}`);
        onImported?.({
          intake_session_id: row.existing_intake_session_id,
          gh_jobsheet_number: row.jobsheet_no,
          created: false,
        });
        return;
      }
      const res = await axios.post(
        `${API}/foh/intake/import-from-garage-hive`,
        { jobsheet_number: row.jobsheet_no },
      );
      toast.success(res.data?.created
        ? `Imported ${row.jobsheet_no} from Garage Hive`
        : `Refreshed ${row.jobsheet_no}`);
      onImported?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
                  : detail?.message || `Couldn't import ${row.jobsheet_no}.`);
    } finally {
      setImporting(null);
    }
  };

  const clearInput = () => { setQuery(""); inputRef.current?.focus(); };

  if (!isOpen) return null;

  const q = (query || "").trim();
  const belowMin  = q.length > 0 && q.length < MIN_CHARS;
  const noResults = !loading && !error && q.length >= MIN_CHARS && results.length === 0;

  return (
    <div
      className="fixed inset-0 z-[75] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center pt-14 px-3"
      role="dialog"
      aria-modal="true"
      data-testid="gh-search-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <Cable className="w-5 h-5 text-teal-700" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 leading-tight">
                Search Garage Hive
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Type whatever you know — jobsheet, registration, customer, make or model.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-1 rounded"
              aria-label="Close"
              data-testid="gh-search-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="relative mt-3">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Registration, jobsheet, customer, make or model"
              className="w-full border border-slate-300 rounded-lg pl-9 pr-9 py-2.5 text-sm focus:ring-2 focus:ring-teal-300 focus:border-teal-500 outline-none"
              data-testid="gh-search-input"
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                onClick={clearInput}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                aria-label="Clear"
                data-testid="gh-search-clear-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results area */}
        <div className="flex-1 overflow-y-auto min-h-[80px] relative">
          {belowMin && (
            <p className="p-6 text-sm text-slate-500 text-center" data-testid="gh-search-below-min">
              Type at least {MIN_CHARS} characters to start searching…
            </p>
          )}
          {loading && (
            <div className="flex items-center gap-2 p-6 text-sm text-slate-500" data-testid="gh-search-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching Garage Hive…
            </div>
          )}
          {error && (
            <div className="p-6 flex items-start gap-2 text-sm text-red-700 bg-red-50 border-b border-red-100"
                 data-testid="gh-search-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
          {noResults && (
            <div className="p-6 text-sm text-slate-600" data-testid="gh-search-empty">
              <p className="font-semibold text-slate-800 mb-1">
                No Garage Hive jobsheets found.
              </p>
              <p className="text-slate-500 leading-relaxed">
                Check the registration, jobsheet number, customer, make or model
                and try again.
              </p>
            </div>
          )}
          {!loading && results.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="gh-search-results">
              {results.map((row, idx) => (
                <ResultRow
                  key={`${row.system_id || row.jobsheet_no}-${idx}`}
                  row={row}
                  isImporting={importing === row.jobsheet_no}
                  disabled={!!importing}
                  onSelect={() => handleSelect(row)}
                  onViewHistory={row.registration_display ? () => setHistoryReg(row.registration_display.replace(/\s+/g, "")) : null}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Footer diagnostics for admins */}
        {diagnostics && !loading && (
          <div className="px-5 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400"
               data-testid="gh-search-diag">
            <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
            <span>{diagnostics.total_ms}ms · {diagnostics.gh_calls} GH call{diagnostics.gh_calls === 1 ? "" : "s"}</span>
          </div>
        )}
      </div>

      {/* Vehicle history slide-in (mounted inside modal so it z-orders correctly) */}
      {historyReg && (
        <VehicleHistoryPanel
          reg={historyReg}
          isOpen={!!historyReg}
          onClose={() => setHistoryReg(null)}
        />
      )}
    </div>
  );
}

function ResultRow({ row, onSelect, onViewHistory, isImporting, disabled }) {
  return (
    <li>
      <div
        className="w-full px-5 py-3 hover:bg-teal-50 transition-colors flex items-start gap-3 group"
        data-testid={`gh-search-result-${row.jobsheet_no}`}
      >
        <button
          type="button"
          onClick={onSelect}
          disabled={disabled}
          className="flex-1 min-w-0 text-left disabled:opacity-60 disabled:cursor-wait"
          data-testid={`gh-search-result-${row.jobsheet_no}-select`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-slate-900" data-testid={`gh-search-result-${row.jobsheet_no}-no`}>
              {row.jobsheet_no}
            </span>
            {row.registration_display && (
              <>
                <span className="text-slate-400">—</span>
                <span className="font-mono text-slate-800">{row.registration_display}</span>
              </>
            )}
            <StatusPill status={row.status} isOpen={row.is_open} />
            {row.existing_intake_session_id && (
              <span className="inline-flex items-center gap-1 rounded-full border border-teal-300 bg-teal-50 text-teal-700 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                    data-testid={`gh-search-result-${row.jobsheet_no}-existing`}>
                <CheckCircle2 className="w-3 h-3" />
                In Yestimate
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
            {row.vehicle_description && (
              <span className="inline-flex items-center gap-1">
                <Car className="w-3 h-3 text-slate-400" />
                {row.vehicle_description}
              </span>
            )}
            {row.customer_name && (
              <span className="inline-flex items-center gap-1">
                <User className="w-3 h-3 text-slate-400" />
                {row.customer_name}
              </span>
            )}
            {(row.jobsheet_date || row.booking_date) && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 text-slate-400" />
                {fmtDate(row.jobsheet_date || row.booking_date)}
              </span>
            )}
          </div>
          {row.work_summary && (
            <p className="text-[11px] text-slate-500 mt-1 truncate">{row.work_summary}</p>
          )}
        </button>
        <div className="shrink-0 self-center flex items-center gap-1">
          {onViewHistory && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewHistory(); }}
              disabled={disabled}
              className="p-1.5 rounded text-slate-500 hover:text-teal-700 hover:bg-teal-100 disabled:opacity-40"
              title="View vehicle history"
              data-testid={`gh-search-result-${row.jobsheet_no}-history`}
            >
              <History className="w-4 h-4" />
            </button>
          )}
          <div>
            {isImporting
              ? <Loader2 className="w-4 h-4 animate-spin text-teal-700" />
              : row.existing_intake_session_id
                ? <ExternalLink className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                : <ArrowRight className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            }
          </div>
        </div>
      </div>
    </li>
  );
}

export default GHSearchModal;
